import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import ImageInput from 'components/field/ImageInput';
import Card from 'components/global/Card';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faBarcode, faCircleCalendar, faLocation, faUser } from '@fortawesome/pro-solid-svg-icons';

import InfoLabel from './component/InfoLabel';
import * as S from './styled/OrderConfirmation.styled';

export const orderConfirmationDefaultValues = [
	{
		type: 'color',
		value: '#F5F5F5',
		name: 'pageBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'navigationBackgroundColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'iconColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'formBackgroundColor'
	},
	{
		type: 'color',
		value: '#70b923',
		name: 'acceptButtonColor'
	},
	{
		type: 'color',
		value: '#b92323',
		name: 'rejectButtonColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'contentTitleColor'
	},
	{
		type: 'color',
		value: '#505050',
		name: 'contentTextColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'headerTextColor'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showCustomerName'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderDate'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showAddress'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showGrandTotal'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showIcons'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnBottom'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnTop'
	}
];

/**
 * @author  Burak Ak
 */

const OrderConfirmation = (props) => {
	const { showError, showSuccess } = useAlert();

	const [isLoading, setIsLoading] = useState(false);
	const {
		orderDate,
		customerName,
		orderNumber,
		status,
		address,
		orderConfirmationStatus,
		propJson,
		totalAmount,
		currency,
		orderItems,
		defaultValues,
		otoOrderID,
		getData
	} = props;

	const formMethods = useForm({
		defaultValues: {
			orderItems: null
		}
	});

	const { watch, reset, setValue, control } = formMethods;

	const { fields } = useFieldArray({
		control,
		name: 'orderItems'
	});

	useEffect(() => {
		if (props) {
			reset({
				orderItems: orderItems ?? null
			});
		}
	}, [props]);

	const statusContainers = [
		{ id: 1, status: 'notConfirmedYet', bg: '#ef7f47', text: 'waitingUserConfirmationMessage' },
		{ id: 2, status: 'confirmCanceled', bg: '#b92323', text: 'orderRejected' },
		{ id: 3, status: 'confirmed', bg: '#2ecc71', text: 'orderConfirmedSuccessfully' }
	];

	const columns = useMemo(
		() => [
			{
				header: 'image',
				accessorKey: 'image',
				size: 25,
				renderCell: ({ row: { index } }) => (
					<ImageInput
						viewMode
						name={`orderItems[${index}].productImage`}
						value={watch(`orderItems[${index}].productImage`)}
						setValue={(value) => {
							setValue(`orderItems[${index}].productImage`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				)
			},
			{
				header: 'name',
				accessorKey: 'name',
				size: 25,
				renderCell: ({ row }) => <Typography>{row.original.productName}</Typography>
			},
			{
				header: 'orderedQuantity',
				accessorKey: 'qty',
				size: 25,
				renderCell: ({ row }) => <Typography>{row.original.qtyOrdered}</Typography>
			}
		],
		[]
	);

	const getStatusContainer = (status, headerBackgroundColor, text, id) => (
		<S.StatusContainer key={id} headerBackgroundColor={headerBackgroundColor}>
			<Typography
				variant="p1"
				fontWeight="500"
				color={propJson?.headerTextColor ? propJson?.headerTextColor : 'white'}>
				{text}
			</Typography>
		</S.StatusContainer>
	);

	const onSubmit = async (status) => {
		setIsLoading(true);
		try {
			const response = await openAxios.post(OPEN.SMS.UPDATE_ORDER_CONFIRMATION, {
				otoOrderID,
				isOrderConfirmed: status
			});
			if (response.status === 200) {
				showSuccess();
				getData?.();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: error?.response?.data?.code || error?.response?.data?.message
			});

			AppLogger.error(error);
		}
		setIsLoading(false);
	};

	return (
		<FormProvider {...formMethods}>
			<S.Card formBackgroundColor={propJson?.formBackgroundColor}>
				{statusContainers.map(
					({ status, bg, text, id }) =>
						status === orderConfirmationStatus && getStatusContainer(status, bg, text, id)
				)}

				<S.DataContent>
					<div className="row row-cols-1 row-cols-lg-2">
						{propJson?.showCustomerName && (
							<InfoLabel
								label="customerName"
								value={customerName}
								icon={propJson?.showIcons && faUser}
								valueFontWeight="700"
								iconSize="x-large"
								labelFontWeight="500"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}

						{propJson?.showOrderNumber && (
							<InfoLabel
								label="orderNumber"
								value={orderNumber}
								icon={propJson?.showIcons && faBarcode}
								valueFontWeight="700"
								iconSize="x-large"
								labelFontWeight="500"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}

						{propJson?.showOrderDate && (
							<InfoLabel
								label="orderDate"
								value={orderDate}
								icon={propJson?.showIcons && faCircleCalendar}
								valueFontWeight="700"
								iconSize="x-large"
								labelFontWeight="500"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}
						{propJson?.showAddress && (
							<InfoLabel
								label="address"
								value={address}
								icon={propJson?.showIcons && faLocation}
								valueFontWeight="700"
								iconSize="x-large"
								labelFontWeight="500"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}
					</div>
				</S.DataContent>
			</S.Card>

			<Card
				background
				border={0}
				shadowed
				rounded
				className="mt-5"
				style={{
					backgroundColor: propJson?.formBackgroundColor
				}}>
				<Table data={fields} columns={columns} />

				{propJson?.showGrandTotal && (
					<S.GrandTotal>
						<InfoLabel
							label="grandTotal"
							value={`: ${totalAmount} ${currency}`}
							labelFontWeight="700"
							labelVariant="p2"
							valueVariant="p2"
							valueFontWeight="700"
							rowMode
							labelColor={propJson?.contentTitleColor}
							valueColor={propJson?.contentTextColor}
						/>
					</S.GrandTotal>
				)}
				{((orderConfirmationStatus === 'notConfirmedYet' && status === 'needConfirmation') ||
					defaultValues === true) && (
					<S.ButtonContainer>
						<Button
							onClick={() => onSubmit(false)}
							size="large"
							loading={isLoading}
							label="cancelMyOrder"
							style={{ border: propJson?.rejectButtonColor, background: propJson?.rejectButtonColor }}
						/>

						<Button
							onClick={() => onSubmit(true)}
							size="large"
							loading={isLoading}
							label="shipMyOrder"
							style={{ border: propJson?.acceptButtonColor, background: propJson?.acceptButtonColor }}
						/>
					</S.ButtonContainer>
				)}
			</Card>
		</FormProvider>
	);
};

export default OrderConfirmation;
